import { Box, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, Radio, RadioGroup, Typography } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { CourseEntity } from 'src/entities/Course.entity';
import { RouteSourceEntity } from 'src/entities/Timetable.entity';
import { useRouteSourcesQuery } from 'src/hooks/useRouteSources.query';

import { courseLabel } from './courseMasterUtils';

export type TimetableRow = { courseId: number; copyFromTimetableId?: number };
type Props = { open: boolean; courses: CourseEntity[]; currentIds: number[]; onClose: () => void; onAdd: (rows: TimetableRow[]) => void };
/** コピー元の取得状態。needs は「選ばないと保存できない」、ready は「取得が成功して再取得中でない」、valid は「選択が今の候補にある（選ばなくてよいなら true）」 */
type SourceStatus = { needs: boolean; ready: boolean; valid: boolean };

export const sourceLabel = (s: RouteSourceEntity) => `${s.timetableName ?? '（名前なし）'}（${s.placeNames.filter(Boolean).join(' → ') || '地点なし'}）`;

/**
 * ダイヤに入れるコースを選ぶ。
 * - 2 便にチェックすると紐付け先の 1 便も入れる（1 便の無い 2 便を作らない。C3 ルール 5 (b)）
 * - 既存のダイヤの並びが違うコースは、どのダイヤの並びをコピーするかを選ぶ（C1 ルール 3。候補は route_sources）。
 *   候補の取得が終わるまで（失敗中も）追加できない。「不要」と誤って決めないため
 */
export default function AddCoursesDialog({ open, courses, currentIds, onClose, onAdd }: Props) {
  const [checked, setChecked] = useState<Map<number, number | undefined>>(new Map());
  const [status, setStatus] = useState<Map<number, SourceStatus>>(new Map());
  const candidates = useMemo(() => courses.filter((c) => !currentIds.includes(c.id)), [courses, currentIds]);
  useEffect(() => { if (!open) { setChecked(new Map()); setStatus(new Map()); } }, [open]);

  const toggle = (course: CourseEntity) => setChecked((prev) => {
    const next = new Map(prev);
    if (next.has(course.id)) { next.delete(course.id); return next; }
    next.set(course.id, undefined);
    const first = course.firstTripCourse;
    if (course.tripNo >= 2 && first && !currentIds.includes(first.id) && !next.has(first.id) && candidates.some((c) => c.id === first.id)) next.set(first.id, undefined);
    return next;
  });
  const onStatus = useCallback((courseId: number, value: SourceStatus) => setStatus((prev) => {
    const current = prev.get(courseId);
    if (current && current.needs === value.needs && current.ready === value.ready && current.valid === value.valid) return prev;
    return new Map(prev).set(courseId, value);
  }), []);
  // チェックを外した行の status は残るが、見るのはチェック中の行だけ
  const blocked = [...checked].some(([id]) => { const st = status.get(id); return !st || !st.ready || !st.valid; });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontSize: 16 }}>ダイヤにコースを追加</DialogTitle>
      <DialogContent>
        {candidates.map((course) => (
          <Box key={course.id} sx={{ borderBottom: 1, borderColor: 'divider', py: 0.5 }}>
            <FormControlLabel
              control={<Checkbox checked={checked.has(course.id)} onChange={() => toggle(course)} />}
              label={`${courseLabel(course)}\u3000${course.name ?? ''}`}
            />
            {checked.has(course.id) && (
              <RouteSourceChooser
                courseId={course.id}
                label={courseLabel(course)}
                value={checked.get(course.id) ?? null}
                onChange={(timetableId) => setChecked((prev) => new Map(prev).set(course.id, timetableId))}
                onStatus={onStatus}
              />
            )}
          </Box>
        ))}
        {candidates.length === 0 && <Typography sx={{ color: 'text.secondary' }}>追加できるコースはありません</Typography>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>キャンセル</Button>
        <Button
          variant="contained"
          disableElevation
          disabled={checked.size === 0 || blocked}
          onClick={() => onAdd([...checked].map(([courseId, copyFromTimetableId]) => ({ courseId, copyFromTimetableId })))}
        >
          追加する
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function RouteSourceChooser({ courseId, label, value, onChange, onStatus }: {
  courseId: number; label: string; value: number | null; onChange: (timetableId: number | undefined) => void; onStatus: (courseId: number, value: SourceStatus) => void;
}) {
  const sources = useRouteSourcesQuery(courseId);
  const needs = Boolean(sources.data && !sources.data.unassigned && !sources.data.identical && sources.data.sources.length >= 2);
  // Why not Boolean(sources.data): キャッシュを持った再取得中・再取得失敗中も data は残るので、成功かつ取得中でないことを見る
  const ready = sources.isSuccess && !sources.isFetching;
  // 「選ばなくてよい」と「指定したコピー元が今の候補にある」は別: API は選択不要でも候補外の copy_from_timetable_id を 422 にする
  const selectedExists = Boolean(sources.data?.sources.some((s) => s.timetableId === value));
  const valid = (value == null || selectedExists) && (!needs || selectedExists);
  useEffect(() => { onStatus(courseId, { needs, ready, valid }); }, [courseId, needs, ready, valid, onStatus]);
  // 候補から消えた指定は親の Map から外す（選択不要になった場合は undefined で追加できる）
  useEffect(() => { if (ready && value != null && !selectedExists) onChange(undefined); }, [ready, value, selectedExists, onChange]);
  if (sources.isError) {
    return (
      <Box sx={{ pl: 4, pb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography sx={{ fontSize: 13, color: 'error.main' }}>並びのコピー元を取得できませんでした</Typography>
        <Button size="small" aria-label={`${label}のコピー元を取り直す`} onClick={() => { void sources.refetch(); }}>取り直す</Button>
      </Box>
    );
  }
  if (sources.isFetching && !sources.data) return <Typography sx={{ pl: 4, pb: 1, fontSize: 13, color: 'text.secondary' }}>並びを確認中…</Typography>;
  if (!needs || !sources.data) return null;
  return (
    <Box sx={{ pl: 4, pb: 1 }}>
      <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>ダイヤごとに並びが違います。どの並びをコピーしますか？</Typography>
      <RadioGroup aria-label={`${label}の並びのコピー元`} value={value ?? ''} onChange={(e) => onChange(Number(e.target.value))}>
        {sources.data.sources.map((s) => <FormControlLabel key={s.timetableId} value={s.timetableId} control={<Radio size="small" />} label={sourceLabel(s)} />)}
      </RadioGroup>
    </Box>
  );
}
